import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Container,
    Modal,
    IconButton,
    Paper
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import SchoolIcon from '@mui/icons-material/School';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import GroupsIcon from '@mui/icons-material/Groups';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';

const Academy = () => {
    const navigate = useNavigate();
    const [videoModalOpen, setVideoModalOpen] = useState(false);

    const logoColors = {
        primaryBlue: '#3f51b5', // Main blue from logo
        brightYellow: '#c4d835', // Bright yellow/lime from logo
        darkBlue: '#2c387e', // Darker blue for depth
        lightYellow: '#d4e157', // Lighter yellow for highlights
    };

    const academyFeatures = [
        {
            icon: <SchoolIcon sx={{ fontSize: 40, color: logoColors.brightYellow }} />,
            title: "Professional Training",
            description: "Expert coaches with years of experience in futsal and football training programs"
        },
        {
            icon: <GroupsIcon sx={{ fontSize: 40, color: logoColors.brightYellow }} />,
            title: "Age Groups",
            description: "Training programs for all ages from kids (6+) to adults with specialized curriculums"
        },
        {
            icon: <EmojiEventsIcon sx={{ fontSize: 40, color: logoColors.brightYellow }} />,
            title: "Tournaments",
            description: "Regular tournaments and competitions to showcase skills and build competitive spirit"
        },
        {
            icon: <SportsSoccerIcon sx={{ fontSize: 40, color: logoColors.brightYellow }} />,
            title: "Skill Development",
            description: "Focus on technical skills, tactical awareness, physical fitness, and mental strength"
        }
    ];

    const trainingPrograms = [
        {
            title: "Kids Academy (Ages 6-12)",
            description: "Fun-based learning with basic ball control, coordination, and teamwork",
            duration: "2 sessions per week • 1 hour each",
            price: "Contact for pricing"
        },
        {
            title: "Youth Development (Ages 13-17)",
            description: "Advanced skills training with tactical understanding and competitive play",
            duration: "3 sessions per week • 1.5 hours each",
            price: "Contact for pricing"
        },
        {
            title: "Adult Training (Ages 18+)",
            description: "Professional level training focusing on fitness, skills, and match preparation",
            duration: "2-3 sessions per week • 2 hours each",
            price: "Contact for pricing"
        },
        {
            title: "Elite Program",
            description: "High-performance training for advanced players aiming for professional level",
            duration: "Daily training • 2-3 hours each",
            price: "Contact for pricing"
        }
    ];

    const handleVideoOpen = () => {
        setVideoModalOpen(true);
    };

    const handleVideoClose = () => {
        setVideoModalOpen(false);
    };

    return (
        <Box sx={{ minHeight: '100vh' }}>
            {/* Hero Section */}
            <Box
                sx={{
                    position: 'relative',
                    minHeight: '80vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    background: 'transparent',
                    py: 8
                }}
            >
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            background: `linear-gradient(135deg, ${logoColors.primaryBlue}f0 0%, ${logoColors.darkBlue}f0 100%)`,
                            backdropFilter: 'blur(20px)',
                            borderRadius: '24px',
                            border: `2px solid ${logoColors.brightYellow}40`,
                            p: { xs: 4, md: 8 },
                            boxShadow: `0 20px 60px ${logoColors.primaryBlue}30`,
                            textAlign: 'center',
                            color: 'white',
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                    >
                        <Typography 
                            variant="h1" 
                            component="h1" 
                            gutterBottom 
                            sx={{
                                fontWeight: 800,
                                fontSize: { xs: '2.5rem', md: '4rem' },
                                background: `linear-gradient(45deg, ${logoColors.brightYellow} 30%, ${logoColors.lightYellow} 90%)`,
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                letterSpacing: '-0.02em',
                                mb: 2
                            }}
                        >
                            FC ROVERS ACADEMY
                        </Typography>

                        <Typography 
                            variant="h4" 
                            sx={{
                                color: logoColors.brightYellow,
                                fontWeight: 600,
                                fontSize: { xs: '1.3rem', md: '2rem' },
                                mb: 3,
                                textShadow: `0 2px 4px ${logoColors.darkBlue}80`
                            }}
                        >
                            Developing Future Champions
                        </Typography>
                        
                        <Typography 
                            variant="h6" 
                            sx={{
                                color: logoColors.lightYellow,
                                maxWidth: 800,
                                mx: 'auto',
                                mb: 6,
                                fontSize: { xs: '1rem', md: '1.2rem' },
                                lineHeight: 1.8,
                                fontWeight: 400,
                                textShadow: `0 2px 4px ${logoColors.darkBlue}80`
                            }}
                        >
                            Join Karachi's premier futsal academy where passion meets professional training. 
                            Our experienced coaches help players of all levels reach their full potential in a 
                            supportive and competitive environment.
                        </Typography>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                            <Button 
                                variant="contained" 
                                size="large" 
                                onClick={handleVideoOpen}
                                startIcon={<PlayArrowIcon />}
                                sx={{ 
                                    px: 6, 
                                    py: 2,
                                    fontSize: '1.1rem',
                                    fontWeight: 700,
                                    borderRadius: '12px',
                                    background: `linear-gradient(45deg, ${logoColors.brightYellow} 30%, ${logoColors.lightYellow} 90%)`,
                                    color: logoColors.darkBlue,
                                    boxShadow: `0 8px 30px ${logoColors.brightYellow}40`,
                                    textTransform: 'none',
                                    '&:hover': {
                                        transform: 'translateY(-3px)',
                                        boxShadow: `0 12px 40px ${logoColors.brightYellow}50`,
                                        background: `linear-gradient(45deg, ${logoColors.lightYellow} 30%, ${logoColors.brightYellow} 90%)`,
                                    },
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                }}
                            >
                                Watch Academy Video
                            </Button>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* Features Section */}
            <Box sx={{ py: 8 }}>
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            background: `linear-gradient(135deg, ${logoColors.primaryBlue}f0 0%, ${logoColors.darkBlue}f0 100%)`,
                            borderRadius: '24px',
                            p: { xs: 4, md: 6 },
                            textAlign: 'center',
                            color: logoColors.lightYellow,
                            backdropFilter: 'blur(10px)',
                            boxShadow: `0 20px 60px ${logoColors.primaryBlue}30`,
                            border: `2px solid ${logoColors.brightYellow}30`,
                            mb: 8
                        }}
                    >
                        <Typography 
                            variant="h3" 
                            component="h2" 
                            gutterBottom
                            sx={{ 
                                fontWeight: 800,
                                mb: 5,
                                fontSize: { xs: '2rem', md: '3rem' },
                                background: `linear-gradient(45deg, ${logoColors.brightYellow} 30%, ${logoColors.lightYellow} 90%)`,
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}
                        >
                            Why Choose Our Academy?
                        </Typography>
                        
                        <Grid container spacing={4} sx={{ mt: 2 }}>
                            {academyFeatures.map((feature, index) => (
                                <Grid item xs={12} md={6} key={index}>
                                    <Card
                                        sx={{
                                            height: '100%',
                                            background: `${logoColors.primaryBlue}20`,
                                            backdropFilter: 'blur(10px)',
                                            border: `2px solid ${logoColors.brightYellow}40`,
                                            borderRadius: '16px',
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': {
                                                transform: 'translateY(-8px)',
                                                border: `2px solid ${logoColors.brightYellow}80`,
                                                boxShadow: `0 20px 40px ${logoColors.primaryBlue}40`,
                                            }
                                        }}
                                    >
                                        <CardContent sx={{ p: 4, textAlign: 'center' }}>
                                            <Box sx={{ mb: 3 }}>
                                                {feature.icon}
                                            </Box>
                                            <Typography 
                                                variant="h5" 
                                                component="h3" 
                                                sx={{ 
                                                    color: logoColors.brightYellow,
                                                    fontWeight: 700,
                                                    mb: 2
                                                }}
                                            >
                                                {feature.title}
                                            </Typography>
                                            <Typography 
                                                variant="body1" 
                                                sx={{ 
                                                    color: logoColors.lightYellow,
                                                    lineHeight: 1.6,
                                                    fontSize: '1.1rem'
                                                }}
                                            >
                                                {feature.description}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Container>
            </Box>

            {/* Training Programs */}
            <Box sx={{ py: 8 }}>
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            background: `linear-gradient(135deg, ${logoColors.primaryBlue}f0 0%, ${logoColors.darkBlue}f0 100%)`,
                            borderRadius: '24px',
                            p: { xs: 4, md: 6 },
                            backdropFilter: 'blur(10px)',
                            boxShadow: `0 20px 60px ${logoColors.primaryBlue}30`,
                            border: `2px solid ${logoColors.brightYellow}30`,
                        }}
                    >
                        <Typography 
                            variant="h3" 
                            component="h2" 
                            gutterBottom
                            sx={{ 
                                fontWeight: 800,
                                mb: 5,
                                fontSize: { xs: '2rem', md: '3rem' },
                                textAlign: 'center',
                                background: `linear-gradient(45deg, ${logoColors.brightYellow} 30%, ${logoColors.lightYellow} 90%)`,
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}
                        >
                            Training Programs
                        </Typography>
                        
                        <Grid container spacing={4}>
                            {trainingPrograms.map((program, index) => (
                                <Grid item xs={12} md={6} key={index}>
                                    <Card
                                        sx={{
                                            height: '100%',
                                            background: `${logoColors.primaryBlue}20`,
                                            backdropFilter: 'blur(10px)',
                                            border: `2px solid ${logoColors.brightYellow}40`,
                                            borderRadius: '16px',
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': {
                                                transform: 'translateY(-8px)',
                                                border: `2px solid ${logoColors.brightYellow}80`,
                                                boxShadow: `0 20px 40px ${logoColors.primaryBlue}40`,
                                            }
                                        }}
                                    >
                                        <CardContent sx={{ p: 4 }}>
                                            <Typography 
                                                variant="h5" 
                                                component="h3" 
                                                sx={{ 
                                                    color: logoColors.brightYellow,
                                                    fontWeight: 700,
                                                    mb: 2
                                                }}
                                            >
                                                {program.title}
                                            </Typography>
                                            <Typography 
                                                variant="body1" 
                                                sx={{ 
                                                    color: logoColors.lightYellow,
                                                    lineHeight: 1.6,
                                                    mb: 2,
                                                    fontSize: '1rem'
                                                }}
                                            >
                                                {program.description}
                                            </Typography>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    color: logoColors.brightYellow,
                                                    fontWeight: 600,
                                                    mb: 1
                                                }}
                                            >
                                                Duration: {program.duration}
                                            </Typography>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    color: logoColors.lightYellow,
                                                    fontStyle: 'italic'
                                                }}
                                            >
                                                {program.price}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Container>
            </Box>

           {/* Contact Section */}
<Box sx={{ py: 8 }}>
    <Container maxWidth="lg">
        <Box
            sx={{
                background: `linear-gradient(135deg, ${logoColors.primaryBlue}f0 0%, ${logoColors.darkBlue}f0 100%)`,
                borderRadius: '24px',
                p: { xs: 4, md: 8 },
                textAlign: 'center',
                color: logoColors.lightYellow,
                backdropFilter: 'blur(10px)',
                boxShadow: `0 20px 60px rgba(59, 76, 192, 0.3)`,
                border: `2px solid rgba(255, 215, 0, 0.3)`,
            }}
        >
            <Typography 
                variant="h3" 
                component="h2" 
                gutterBottom
                sx={{ 
                    fontWeight: 800,
                    mb: 3,
                    fontSize: { xs: '2rem', md: '3rem' },
                    background: `linear-gradient(45deg, ${logoColors.brightYellow} 30%, ${logoColors.lightYellow} 90%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                }}
            >
                Ready to Start Your Journey?
            </Typography>
            
            <Typography 
                variant="h6" 
                sx={{ 
                    mb: 6,
                    opacity: 0.95,
                    lineHeight: 1.7,
                    fontSize: { xs: '1.1rem', md: '1.3rem' },
                    maxWidth: 700,
                    mx: 'auto',
                    color: logoColors.lightYellow
                }}
            >
                Contact us today to register for our academy programs and take the first step 
                towards becoming a better player. Our team is ready to help you choose the 
                right program for your skill level and goals.
            </Typography>
            
            {/* Contact Methods */}
            <Grid container spacing={4} sx={{ mt: 4, justifyContent: 'center', maxWidth: 800, mx: 'auto' }}>
                {/* WhatsApp */}
                <Grid item xs={12} sm={6} md={5}>
                    <Paper
                        elevation={0}
                        sx={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(20px)',
                            border: '2px solid rgba(255, 215, 0, 0.3)',
                            borderRadius: '20px',
                            p: 3,
                            textAlign: 'center',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            cursor: 'pointer',
                            minHeight: 200,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            '&:hover': {
                                transform: 'translateY(-8px)',
                                border: '2px solid rgba(255, 215, 0, 0.6)',
                                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                                background: 'rgba(255, 255, 255, 0.15)',
                            }
                        }}
                        component="a"
                        href="https://wa.me/923238148153"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none' }}
                    >
                        <WhatsAppIcon 
                            sx={{ 
                                fontSize: 48, 
                                color: '#25D366',
                                mb: 2,
                                mx: 'auto'
                            }} 
                        />
                        <Typography 
                            variant="h6" 
                            sx={{ 
                                color: logoColors.brightYellow,
                                fontWeight: 700,
                                mb: 1
                            }}
                        >
                            WhatsApp Registration
                        </Typography>
                        <Typography 
                            variant="body1" 
                            sx={{ 
                                color: logoColors.lightYellow,
                                fontWeight: 600,
                                fontSize: '1.1rem',
                                mb: 1
                            }}
                        >
                            +92 323 8148153
                        </Typography>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                color: logoColors.lightYellow,
                                opacity: 0.9
                            }}
                        >
                            Quick registration & queries
                        </Typography>
                    </Paper>
                </Grid>
                
                {/* Phone */}
                {/* <Grid item xs={12} sm={6} md={5}>
                    <Paper
                        elevation={0}
                        sx={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(20px)',
                            border: '2px solid rgba(255, 215, 0, 0.3)',
                            borderRadius: '20px',
                            p: 3,
                            textAlign: 'center',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            cursor: 'pointer',
                            minHeight: 200,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            '&:hover': {
                                transform: 'translateY(-8px)',
                                border: '2px solid rgba(255, 215, 0, 0.6)',
                                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                                background: 'rgba(255, 255, 255, 0.15)',
                            }
                        }}
                        component="a"
                        href="tel:+923238148153"
                        style={{ textDecoration: 'none' }}
                    >
                        <PhoneIcon 
                            sx={{ 
                                fontSize: 48, 
                                color: logoColors.brightYellow,
                                mb: 2,
                                mx: 'auto'
                            }} 
                        />
                        <Typography 
                            variant="h6" 
                            sx={{ 
                                color: logoColors.brightYellow,
                                fontWeight: 700,
                                mb: 1
                            }}
                        >
                            Call for Details
                        </Typography>
                        <Typography 
                            variant="body1" 
                            sx={{ 
                                color: logoColors.lightYellow,
                                fontWeight: 600,
                                fontSize: '1.1rem',
                                mb: 1
                            }}
                        >
                            +92 323 8148153
                        </Typography>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                color: logoColors.lightYellow,
                                opacity: 0.9
                            }}
                        >
                            Direct consultation
                        </Typography>
                    </Paper>
                </Grid> */}
            </Grid>
            
            <Typography 
                variant="h5" 
                sx={{ 
                    mt: 6,
                    color: logoColors.brightYellow,
                    fontWeight: 600,
                    fontSize: { xs: '1.2rem', md: '1.5rem' }
                }}
            >
                Academy Hours: Daily 4:00 PM - 10:00 PM
            </Typography>
        </Box>
    </Container>
</Box>

            {/* Video Modal */}
            <Modal
                open={videoModalOpen}
                onClose={handleVideoClose}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        // width: '90vw',
                        maxWidth: '800px',
                        maxHeight: '90vh',
                        outline: 'none',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        boxShadow: `0 25px 50px ${logoColors.primaryBlue}50`,
                        border: `2px solid ${logoColors.brightYellow}60`
                    }}
                >
                    <video
                        controls
                        autoPlay
                        style={{
                            width: 'auto',
                            height: 'auto',
                            display: 'block',
                            backgroundColor: '#000'
                        }}
                        onError={(e) => {
                            console.error('Video failed to load:', e);
                        }}
                    >
                        <source src="/malir-draft-3.mp4" type="video/mp4" />
                        {/* <source src="/academy-video.webm" type="video/webm" /> */}
                        Your browser does not support the video tag.
                    </video>
                    
                    {/* Close Button */}
                    <IconButton
                        onClick={handleVideoClose}
                        sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            background: `${logoColors.primaryBlue}cc`,
                            color: logoColors.brightYellow,
                            '&:hover': {
                                background: `${logoColors.darkBlue}cc`,
                                color: logoColors.lightYellow
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Modal>
        </Box>
    );
};

export default Academy;